import { Link } from "gatsby";
import PropTypes from "prop-types";
import React from "react";
import SEO from "../../components/seo";

const pageHeading = {
	fontFamily: "var(--heading-font)",
	fontWeight: 800,
	fontSize: 32,
	lineHeight: 1.2,
	color: "var(--text)",
	margin: "0 0 16px",
};

const introText = {
	fontFamily: "var(--body-font)",
	fontSize: 17,
	lineHeight: 1.7,
	color: "var(--text-muted)",
	margin: "0 0 40px",
};

const InfoCard = ({ bg, icon, title, text, to }) => (
	<Link
		to={to}
		className="card-hover"
		style={{
			background: "var(--surface)",
			border: "1px solid var(--border)",
			borderRadius: 14,
			padding: "32px 26px",
			display: "flex",
			flexDirection: "column",
			gap: 14,
			textDecoration: "none",
		}}
	>
		<div
			style={{
				width: 56,
				height: 56,
				borderRadius: "50%",
				background: bg,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			{icon}
		</div>
		<h2 style={{ fontFamily: "var(--heading-font)", fontWeight: 700, fontSize: 18, color: "var(--text)", margin: 0 }}>
			{title}
		</h2>
		<p style={{ fontFamily: "var(--body-font)", fontSize: 14.5, lineHeight: 1.6, color: "var(--text-muted)", margin: 0 }}>
			{text}
		</p>
		<span
			style={{
				fontFamily: "var(--body-font)",
				fontSize: 14,
				fontWeight: 600,
				color: "var(--primary-dark)",
				marginTop: "auto",
			}}
		>
			View details &rarr;
		</span>
	</Link>
);

InfoCard.propTypes = {
	bg: PropTypes.string.isRequired,
	icon: PropTypes.node.isRequired,
	title: PropTypes.string.isRequired,
	text: PropTypes.string.isRequired,
	to: PropTypes.string.isRequired,
};

const iconProps = {
	width: 24,
	height: 24,
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "#fff",
	strokeWidth: "2",
	strokeLinecap: "round",
	strokeLinejoin: "round",
};

const PatientInformation = () => {
	return (
		<div>
			<SEO
				title="Patient Information"
				keywords={[
					`New patients`,
					`Patient information`,
					`Gastroenterologist`,
					`Hepatologist`,
					`Dr Frank`,
					`Dr Brennan`,
					`Dr Spiro`,
					`Dr Raftopoulos`,
					`Dr Ian Yusoff`,
					`Dr Dev Segarajasingam`,
					`Dr Sam Rao`,
					`Dr Duy Pham`,
					`Dr Claire Gordon`,
				]}
				description="Patient information - things you need to know before you come in"
			/>

			<h1 style={pageHeading}>Patient Information</h1>
			<p style={introText}>Everything you need to know before your visit — from what to bring, to fees, to how a referral reaches us.</p>

			<div
				className="services-grid"
				style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24, marginBottom: 24 }}
			>
				<InfoCard
					bg="var(--primary)"
					to="/patient-information/first-visit"
					title="First Visit"
					text="What to bring and how to prepare for your first appointment."
					icon={(
						<svg {...iconProps}>
							<path d="M9 11l3 3L22 4" />
							<path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
						</svg>
					)}
				/>
				<InfoCard
					bg="var(--warm)"
					to="/patient-information/fees-and-charges"
					title="Fees and Charges"
					text="Information on billing, gap fees, and payment options."
					icon={(
						<svg {...iconProps}>
							<circle cx="12" cy="12" r="9" />
							<path d="M12 7v10M15 9.5c0-1.4-1.3-2.5-3-2.5s-3 1-3 2.3c0 3.2 6 1.5 6 4.7 0 1.3-1.3 2.3-3 2.3s-3-1.1-3-2.5" />
						</svg>
					)}
				/>
				<InfoCard
					bg="var(--primary-dark)"
					to="/patient-information/new-referrals"
					title="New Referrals"
					text="How your GP can send a referral, and what happens next."
					icon={(
						<svg {...iconProps}>
							<path d="M22 6 12 13 2 6" />
							<rect x="2" y="4" width="20" height="16" rx="2" />
						</svg>
					)}
				/>
			</div>
		</div>
	);
};

export default PatientInformation;
